<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';
import { useRoute } from 'vue-router';

import { ScreenSizeType } from '@apps/main/new-desktop/core/models';

import { performanceMark } from '@libs/utils';

import { useScreenSizeStore } from '../../core/stores';

import HotelSelectionContentDesktop from './components/HotelSelectionContentDesktop.vue';
import HotelSelectionContentMobile from './components/HotelSelectionContentMobile.vue';
import HotelSelectionDesktopLayout from './components/HotelSelectionDesktopLayout.vue';
import HotelSelectionHeaderDesktop from './components/HotelSelectionHeaderDesktop.vue';
import HotelSelectionHeaderMobile from './components/HotelSelectionHeaderMobile.vue';
import HotelSelectionMobileLayout from './components/HotelSelectionMobileLayout.vue';
import HotelSelectionModals from './components/HotelSelectionModals.vue';
import ListMapSwitch from './components/ListMapSwitch.vue';
import { useHotelSelectionStore } from './stores';
import { HotelSelectionViewType } from './models';

const screenSizeStore = useScreenSizeStore();
const hotelSelectionStore = useHotelSelectionStore();
const route = useRoute();

onMounted(() => {
  performanceMark('HotelSelectionViewInitiated');

  const index = route.params.recipeIndex;

  if (typeof index === 'string') {
    const indexAsNumber = parseInt(index);
    hotelSelectionStore.initializeHotelSelectionView(indexAsNumber);
  }
});

onUnmounted(() => {
  hotelSelectionStore.resetHotelSelectionView();
});

const hasInitializeHotelSelectionList = ref<boolean>(false);

function onInitializeHotelSelectionList(): void {
  if (hasInitializeHotelSelectionList.value) {
    return;
  }
  hotelSelectionStore.handleInitializedEvent();
  hasInitializeHotelSelectionList.value = true;
}
</script>

<template>
  <!-- NOTE: Hotel details panel is mounted via child route -->
  <router-view />

  <HotelSelectionModals />

  <HotelSelectionMobileLayout
    v-if="screenSizeStore.isNarrowerThan(ScreenSizeType.Medium)"
    :has-fixed-height="
      hotelSelectionStore.viewType === HotelSelectionViewType.Map
    "
    @long-scroll="hotelSelectionStore.hidePriceCalendar"
  >
    <template #header>
      <HotelSelectionHeaderMobile />
    </template>
    <template #content>
      <HotelSelectionContentMobile
        @initialized-hotel-selection-list="onInitializeHotelSelectionList()"
      />
    </template>
    <template #list-map-switch>
      <ListMapSwitch />
    </template>
  </HotelSelectionMobileLayout>

  <HotelSelectionDesktopLayout v-else>
    <template #upper-panel>
      <HotelSelectionHeaderDesktop />
    </template>
    <template #lower-panel>
      <HotelSelectionContentDesktop
        @initialized-hotel-selection-list="onInitializeHotelSelectionList()"
      />
    </template>
  </HotelSelectionDesktopLayout>
</template>
